import React from 'react';
import type {
  ColDef,
  EditableCallbackParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getRoles, saveBatchRoles } from '../../api/roles';
import type { Role } from '../../types/Role';
import { t } from '../../i18n/t';
import { formatDateTimeIL } from '../../utils/dateTime';

type RowStatusData = { __status?: string } | undefined;

const isAddedRow = (data: RowStatusData) => data?.__status === 'added';

const rolesColumnDefs: ColDef<Role>[] = [
  {
    field: 'roleId',
    headerName: t('roles.columns.roleId'),
    width: 90,
    editable: false,
    // Auto-generated by the server; show empty until the added row is saved.
    valueFormatter: (p: ValueFormatterParams<Role>) =>
      isAddedRow(p.data as RowStatusData) || p.value == null ? '' : String(p.value),
  },
  {
    field: 'roleName',
    headerName: t('roles.columns.roleName'),
    flex: 1,
    // Editable for new rows and non-built-in roles; frozen on built-ins.
    editable: (params: EditableCallbackParams<Role>) => !params.data?.isBuiltIn,
  },
  {
    field: 'description',
    headerName: t('roles.columns.description'),
    flex: 2,
    // The one field editable even on built-in roles.
    editable: true,
  },
  {
    field: 'isActive',
    headerName: t('roles.columns.isActive'),
    width: 90,
    editable: (params: EditableCallbackParams<Role>) => !params.data?.isBuiltIn,
    cellDataType: 'boolean',
  },
  {
    field: 'isBuiltIn',
    headerName: t('roles.columns.isBuiltIn'),
    width: 130,
    editable: false,
    cellDataType: 'boolean',
  },
  {
    field: 'isSuperAdmin',
    headerName: t('roles.columns.isSuperAdmin'),
    width: 110,
    editable: false,
    cellDataType: 'boolean',
  },
  {
    field: 'createdAt',
    headerName: t('roles.columns.createdAt'),
    width: 150,
    editable: false,
    valueFormatter: (p: ValueFormatterParams<Role>) =>
      formatDateTimeIL(p.value),
  },
];

const newRoleDefaults = (): Partial<Role> => ({
  roleId: 0,
  roleName: '',
  description: '',
  isBuiltIn: false,
  isActive: true,
  isSuperAdmin: false,
  createdAt: new Date().toISOString(),
});

export function RolesPage() {
  return (
    <LopCrudPage<Role>
      resourceCode="roles"
      titleKey="roles.pageTitle"
      idField="roleId"
      getRows={getRoles}
      saveBatch={(added, modified, deletedIds) =>
        // Send full rows; the server applies the COALESCE / "null = no change"
        // semantics by trim-equality, so the client never diffs fields itself.
        saveBatchRoles({
          added,
          modified,
          deleted: deletedIds as number[],
        })
      }
      columnDefs={rolesColumnDefs}
      newRowDefaults={newRoleDefaults}
    />
  );
}
